import { useState, useEffect, useCallback } from 'react'
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
} from 'lexical'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import './LexicalEditor.scss'
import AddFilePlugin from './Plugins/AddFilePlugin'
import { AgxTypography } from '@urbanx/agx.ui-components2'
import AddLinkPlugin from './Plugins/AddLinkPlugin'
import clsx from 'clsx'

const URL_MATCHER = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

const MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text)
    if (!match) return null
    const fullMatch = match[0]
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    }
  },
]

interface LexicalEditorProps {
  id: string
  label?: string
  defaultValue?: string
  onChange?: (html: string, text: string) => void
  maxLength?: number
  stretch?: boolean
  placeholder?: string
}

const InitialContentPlugin = ({ defaultValue }: { defaultValue?: string }) => {
  const [editor] = useLexicalComposerContext();
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!hasLoaded) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();

        if (defaultValue && defaultValue.trim() !== '') {
          const parser = new DOMParser();
          const dom = parser.parseFromString(defaultValue, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          root.append(...nodes);
        } else {
          root.append($createParagraphNode());
        }
      });
      setHasLoaded(true);
    }
  }, [defaultValue, editor, hasLoaded]);

  return null;
}

const CharacterLimitPlugin = ({ maxLength }: { maxLength: number }) => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const checkAndHandleInput = (inputData: string, event?: Event) => {
      let shouldBlock = false
      
      editor.getEditorState().read(() => {
        const textContent = $getRoot().getTextContent()
        const selection = $getSelection()
        
        if ($isRangeSelection(selection)) {
          const selectedLength = selection.getTextContent().length
          const newLength = textContent.length - selectedLength + inputData.length
          
          if (newLength > maxLength) {
            shouldBlock = true
            const remainingChars = maxLength - (textContent.length - selectedLength)
            
            if (remainingChars > 0) {
              editor.update(() => {
                const currentSelection = $getSelection()
                if ($isRangeSelection(currentSelection)) {
                  const truncatedInput = inputData.substring(0, remainingChars)
                  currentSelection.insertText(truncatedInput)
                }
              })
            }
          }
        }
      })
      
      if (shouldBlock && event) {
        event.preventDefault()
        event.stopPropagation()
      }
      
      return shouldBlock
    }

    // Handle Enter key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        checkAndHandleInput('\n', event)
      }
    }

    const handleInput = (event: Event) => {
      const inputEvent = event as InputEvent
      const clipboardEvent = event as ClipboardEvent
      
      let inputData = ''
      
      switch (event.type) {
        case 'paste':
          inputData = clipboardEvent.clipboardData?.getData('text/plain') || ''
          break
        case 'beforeinput':
          if (inputEvent.inputType === 'insertParagraph' || inputEvent.inputType === 'insertLineBreak') {
            return
          }
          inputData = inputEvent.data || ''
          break
        default:
          return
      }
      
      const relevantInputTypes = [
        'insertText', 
        'insertCompositionText'
      ]
      
      if (event.type === 'paste' || 
          (event.type === 'beforeinput' && relevantInputTypes.includes(inputEvent.inputType))) {
        checkAndHandleInput(inputData, event)
      }
    }

    const removePasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event: Event) => {
        const clipboardEvent = event as ClipboardEvent
        const clipboardData = clipboardEvent.clipboardData?.getData('text/plain') || ''
        return checkAndHandleInput(clipboardData)
      },
      COMMAND_PRIORITY_HIGH
    )

    const contentEditableElement = editor.getRootElement()
    if (contentEditableElement) {
      contentEditableElement.addEventListener('paste', handleInput, true)
      contentEditableElement.addEventListener('beforeinput', handleInput, true)
      contentEditableElement.addEventListener('keydown', handleKeyDown, true)
    }

    return () => {
      removePasteCommand()
      if (contentEditableElement) {
        contentEditableElement.removeEventListener('paste', handleInput, true)
        contentEditableElement.removeEventListener('beforeinput', handleInput, true)
        contentEditableElement.removeEventListener('keydown', handleKeyDown, true)
      }
    }
  }, [editor, maxLength])

  return null
}

const LexicalEditor = ({
  id,
  label,
  defaultValue = '',
  onChange,
  maxLength = 500,
  stretch = false,
  placeholder = 'Enter content here'
}: LexicalEditorProps) => {
  const [characterCount, setCharacterCount] = useState(0)

  const initialConfig = {
    namespace: 'agx-lexical-editor',
    theme: {
      paragraph: 'agx-lexical-paragraph',
      link: 'agx-lexical-link',
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error)
    },
    nodes: [LinkNode, AutoLinkNode]
  }

  const handleChange = useCallback(
    (editorState: any, editor: any) => {
      editorState.read(() => {
        const root = $getRoot()
        const textContent = root.getTextContent()

        try {
          const htmlContent = $generateHtmlFromNodes(editor)

          setCharacterCount(textContent.length)

          if (onChange) {
            onChange(htmlContent, textContent)
          }
        } catch (error) {
          console.error('Error generating HTML:', error)
          if (onChange) {
            onChange('', textContent)
          }
        }
      })
    },
    [onChange]
  )

  const containerClasses = clsx('agx-lexical-container', stretch && 'agx-lexical-stretch')

  return (
    <div className={containerClasses}>
      {label && (
        <AgxTypography id="lexical-label" type="label" size="medium">{label}</AgxTypography>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <div className="agx-lexical-editor-wrapper">
          <div className="agx-lexical-toolbar">
            <AddLinkPlugin disabled={characterCount >= maxLength} maxLength={maxLength} />
            <AddFilePlugin disabled={characterCount >= maxLength} maxLength={maxLength} />
          </div>
          <div className="agx-lexical-editor-container">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  id={id}
                  className="agx-lexical-content-editable"
                />
              }
              placeholder={
                <div className="agx-lexical-placeholder">{placeholder}</div>
              }
              ErrorBoundary={(error) => <div>Error: {error.children}</div>}
            />

            <OnChangePlugin onChange={handleChange} />
            <HistoryPlugin />
            <LinkPlugin />
            <AutoLinkPlugin matchers={MATCHERS} />
            <InitialContentPlugin defaultValue={defaultValue} />
            {maxLength && <CharacterLimitPlugin maxLength={maxLength} />}
          </div>

          <div className="agx-lexical-character-count">
            <span className={characterCount >= maxLength ? 'agx-lexical-limit-reached' : ''}>
              {maxLength - characterCount} characters left
            </span>
          </div>
        </div>
      </LexicalComposer>
    </div>
  )
}

export default LexicalEditor